import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, Center, HStack, Spinner, useTheme} from '@chakra-ui/react';
import {AdvancedMarker, Map, useMap} from '@vis.gl/react-google-maps';
import {TextG12, TextG14} from '../typography';
import GooglePlacesAutocomplete from '../GooglePlacesAutocomplete';
import Modal from '../Modals/Modal';
import {ToastMessage} from '../toast';
import useAuth from '../../hooks/useAuth';
import useGetUserLocation from '../../hooks/useUserLocation';
import {useGetUserAddresses} from '../../hooks/useUserAddress';
import {reverseGeocode, ReverseGeocodeResult} from '../../endpoints/api';
import type {LocationMode, SelectedLocation} from './index';

const formatCoords = (lat: number, lng: number) => `${lat.toFixed(4)}, ${lng.toFixed(4)}`;

// Prefer a human-recognizable address shape from the reverse-geocode response. Falls back
// progressively (street → neighborhood → city) since Google may omit components for points
// outside named addresses (e.g. parks, water, rural areas).
const formatGeocodeAddress = (r: ReverseGeocodeResult): string | null => {
  const cityState = [r.city, r.state].filter(Boolean).join(', ');
  if (r.street_address) {
    return cityState ? `${r.street_address}, ${cityState}` : r.street_address;
  }
  if (r.neighborhood && cityState) return `${r.neighborhood}, ${cityState}`;
  if (cityState) return cityState;
  return r.neighborhood || r.city || r.state || null;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selected: SelectedLocation;
  onApply: (next: SelectedLocation) => void;
}

interface PinCoords {
  lat: number;
  lng: number;
}

// Fallback map center for users with no home address and no prior selection (San Diego).
const FALLBACK_CENTER: PinCoords = {lat: 32.7157, lng: -117.1611};
const DEFAULT_ZOOM = 15;
// DEMO_MAP_ID renders without a project-configured Map ID; required for AdvancedMarker.
// In prod, swap for a real Map ID set via env to control styling.
const MAP_ID = process.env.GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';

const PickerModal: React.FC<Props> = ({isOpen, onClose, selected, onApply}) => {
  const theme = useTheme();
  const {user} = useAuth();
  const {userAddresses} = useGetUserAddresses({addressType: 'HOME'});
  const homeAddress = (userAddresses?.[0] as any) || null;
  const homeCoords: PinCoords | null = useMemo(() => {
    if (!homeAddress?.latitude || !homeAddress?.longitude) return null;
    return {lat: parseFloat(homeAddress.latitude), lng: parseFloat(homeAddress.longitude)};
  }, [homeAddress]);
  const homeLabel = useMemo(() => {
    if (!homeAddress?.street_address) return 'Home';
    const parts = [
      homeAddress.unit
        ? `${homeAddress.street_address} ${homeAddress.unit}`
        : homeAddress.street_address,
      homeAddress.city?.name || homeAddress.city,
    ].filter(Boolean);
    return parts.join(', ');
  }, [homeAddress]);

  // Draft mode + pin + label. The map always renders `pinCoords`; on Apply we strip coords
  // for 'home' (the backend resolves home from the user's address — we must not echo coords
  // or the backend treats it as an override).
  const [mode, setMode] = useState<LocationMode>(selected.mode);
  const [pinCoords, setPinCoords] = useState<PinCoords | null>(null);
  const [label, setLabel] = useState<string>(selected.label);
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);
  const [gpsRequested, setGpsRequested] = useState(false);

  // Stale-request guard for reverse-geocode: each call bumps the counter; only the latest
  // response is allowed to commit a label / clear loading. Prevents an older drag's address
  // from clobbering a newer drag while the network call is still in flight.
  const geocodeReqIdRef = useRef(0);
  const resolveAddressForPin = useCallback(async (lat: number, lng: number) => {
    geocodeReqIdRef.current += 1;
    const reqId = geocodeReqIdRef.current;
    setIsResolvingAddress(true);
    try {
      const res = await reverseGeocode(lat, lng);
      if (reqId !== geocodeReqIdRef.current) return;
      const formatted = formatGeocodeAddress(res.data.data);
      // Fall back to coords only if Google returned no usable address components for this
      // point (ocean / unnamed area). The loading state has been visible up to this moment.
      setLabel(formatted || formatCoords(lat, lng));
    } catch {
      if (reqId !== geocodeReqIdRef.current) return;
      setLabel(formatCoords(lat, lng));
    } finally {
      if (reqId === geocodeReqIdRef.current) setIsResolvingAddress(false);
    }
  }, []);

  const {data: gpsData, error: gpsError} = useGetUserLocation(gpsRequested);

  // Reset draft to whatever's committed every time the modal opens. Also seed pinCoords
  // from the committed selection (or home address, or fallback).
  useEffect(() => {
    if (!isOpen) return;
    setMode(selected.mode);
    setLabel(selected.label);
    if (selected.latitude != null && selected.longitude != null) {
      setPinCoords({lat: selected.latitude, lng: selected.longitude});
    } else if (selected.mode === 'home' && homeCoords) {
      setPinCoords(homeCoords);
      // The committed label is "Home"; show the actual address in the field for context.
      setLabel(homeLabel);
    } else {
      setPinCoords(null);
    }
    setGpsRequested(false);
    setIsResolvingAddress(false);
    geocodeReqIdRef.current += 1; // invalidate any in-flight lookup from a prior open
  }, [isOpen, selected, homeCoords, homeLabel]);

  // GPS resolution / error handling. Only fires after the user picks the "Current" button.
  useEffect(() => {
    if (!gpsRequested) return;
    if (gpsError) {
      setGpsRequested(false);
      ToastMessage({
        status: 'error',
        text: 'Could not access your location. Check browser permissions.',
      });
      return;
    }
    if (gpsData?.latitude != null && gpsData?.longitude != null) {
      setGpsRequested(false);
      const coords = {lat: gpsData.latitude, lng: gpsData.longitude};
      setPinCoords(coords);
      resolveAddressForPin(coords.lat, coords.lng);
    }
  }, [gpsRequested, gpsData, gpsError, resolveAddressForPin]);

  const handlePickHome = useCallback(() => {
    if (!homeCoords) return;
    setMode('home');
    setPinCoords(homeCoords);
    setLabel(homeLabel);
  }, [homeCoords, homeLabel]);

  const handlePickGps = useCallback(() => {
    setMode('gps');
    setGpsRequested(true);
  }, []);

  const handlePickCustom = useCallback(() => {
    setMode('custom');
    // Don't clear pinCoords — let the user start dragging from wherever the pin is now.
  }, []);

  const handleMarkerDragEnd = useCallback(
    (e: google.maps.MapMouseEvent) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null || lng == null) return;
      setMode('custom');
      setPinCoords({lat, lng});
      // Loading state shows while the address resolves; on completion the label becomes
      // the formatted address (or coords as a last-resort fallback).
      resolveAddressForPin(lat, lng);
    },
    [resolveAddressForPin],
  );

  // Custom-mode only: a stray map-tap shouldn't override programmatic picks (Home / GPS /
  // search). The vis.gl Map onClick payload is `{detail: {latLng: {lat, lng}}}`; typed as
  // `any` because the lib's MapMouseEvent isn't re-exported.
  const handleMapClick = useCallback(
    (e: any) => {
      if (mode !== 'custom') return;
      const lat = e?.detail?.latLng?.lat;
      const lng = e?.detail?.latLng?.lng;
      if (lat == null || lng == null) return;
      setPinCoords({lat, lng});
      resolveAddressForPin(lat, lng);
    },
    [mode, resolveAddressForPin],
  );

  const handlePlaceSelected = useCallback(
    (place: any) => {
      const lat = place?.geometry?.location?.lat?.();
      const lng = place?.geometry?.location?.lng?.();
      if (lat == null || lng == null) {
        ToastMessage({status: 'error', text: 'Could not read that address. Try another.'});
        return;
      }
      setMode('custom');
      setPinCoords({lat, lng});
      // Places autocomplete already provides a formatted address; only fall back to reverse
      // geocode when it's missing (rare).
      if (place.formatted_address) {
        setLabel(place.formatted_address);
      } else {
        resolveAddressForPin(lat, lng);
      }
    },
    [resolveAddressForPin],
  );

  // Apply is only meaningful when (a) the draft has resolved coords (or it's home with a
  // saved home address), and (b) the resolved selection differs from what's already committed.
  const canApply = useMemo(() => {
    // Hold off while the address is still resolving so the committed label is the final one.
    if (isResolvingAddress) return false;
    const draft: SelectedLocation =
      mode === 'home'
        ? {mode: 'home', latitude: null, longitude: null, label: 'Home'}
        : {
            mode,
            latitude: pinCoords?.lat ?? null,
            longitude: pinCoords?.lng ?? null,
            label,
          };
    if (mode !== 'home' && (draft.latitude == null || draft.longitude == null)) return false;
    if (mode === 'home' && !homeCoords) return false;
    return (
      draft.mode !== selected.mode ||
      draft.latitude !== selected.latitude ||
      draft.longitude !== selected.longitude
    );
  }, [mode, pinCoords, label, selected, homeCoords, isResolvingAddress]);

  const handleApply = useCallback(() => {
    const next: SelectedLocation =
      mode === 'home'
        ? {mode: 'home', latitude: null, longitude: null, label: 'Home'}
        : {
            mode,
            latitude: pinCoords!.lat,
            longitude: pinCoords!.lng,
            label,
          };
    onApply(next);
    onClose();
  }, [mode, pinCoords, label, onApply, onClose]);

  return (
    <Modal
      show={isOpen}
      parentCallback={onClose}
      closeButton
      closeButtonPosition="right"
      header={
        <TextG14 fontWeight="700" textAlign="center" w="100%" mt="0">
          Search near…
        </TextG14>
      }
      modalPadding="16px 20px 20px">
      <Box>
        <SegmentedModePicker
          mode={mode}
          homeDisabled={!homeCoords}
          onPickHome={handlePickHome}
          onPickGps={handlePickGps}
          onPickCustom={handlePickCustom}
        />
        {!homeCoords && (
          <TextG12 mt={2} color={theme.colors.grey} textAlign="center">
            Add a home address in your profile to use this.
          </TextG12>
        )}

        <Box mt={4}>
          <GooglePlacesAutocomplete
            value={isResolvingAddress ? 'Looking up address…' : label}
            onPlaceSelected={handlePlaceSelected}
            placeholder="Type an address or place"
            fontFamily="Gordita"
            fontSize="14px"
            lineHeight="20px"
            p="14px 16px"
            border="1px solid #e5e5e5"
            borderRadius="12px"
            _focus={{borderColor: '#1a1a1a'}}
          />
        </Box>

        <Box
          mt={4}
          h="280px"
          borderRadius="12px"
          overflow="hidden"
          border="1px solid #E5E5E5"
          position="relative">
          {gpsRequested ? (
            <Center h="100%" bg="#F8F6F7">
              <Spinner size="md" color="#C9136B" />
            </Center>
          ) : (
            <Map
              mapId={MAP_ID}
              defaultCenter={pinCoords || FALLBACK_CENTER}
              defaultZoom={DEFAULT_ZOOM}
              gestureHandling="greedy"
              disableDefaultUI={false}
              zoomControl={true}
              streetViewControl={false}
              mapTypeControl={false}
              fullscreenControl={false}
              onClick={handleMapClick}>
              {pinCoords && (
                <>
                  <MapPanController target={pinCoords} />
                  <AdvancedMarker
                    position={pinCoords}
                    draggable
                    onDragEnd={handleMarkerDragEnd}
                  />
                </>
              )}
            </Map>
          )}
          {mode === 'custom' && !pinCoords && (
            <Box
              position="absolute"
              top="8px"
              left="50%"
              transform="translateX(-50%)"
              bg="rgba(38, 38, 38, 0.85)"
              color="white"
              px="12px"
              py="6px"
              borderRadius="100px"
              pointerEvents="none">
              <TextG12 mt={0} fontWeight="600" color="white">
                Tap the map to drop a pin
              </TextG12>
            </Box>
          )}
        </Box>

        <HStack mt={4} spacing={3} justify="space-between">
          <Button variant="outline" flex={1} h="44px" borderRadius="100px" onClick={onClose}>
            <TextG14 mt={0} fontWeight="600" color="#262626">
              Cancel
            </TextG14>
          </Button>
          <Button
            flex={1}
            h="44px"
            borderRadius="100px"
            bg="#C9136B"
            _hover={{bg: '#B10F5C'}}
            _disabled={{bg: '#E5E5E5', cursor: 'not-allowed'}}
            isDisabled={!canApply}
            onClick={handleApply}>
            <TextG14 mt={0} fontWeight="600" color="white">
              Apply
            </TextG14>
          </Button>
        </HStack>
      </Box>
    </Modal>
  );
};

interface SegmentedProps {
  mode: LocationMode;
  homeDisabled: boolean;
  onPickHome: () => void;
  onPickGps: () => void;
  onPickCustom: () => void;
}

// Three pill-buttons in one row. The active one is filled with primary; others are
// transparent. Disabled state for "Home" is rendered with reduced opacity and ignored taps.
const SegmentedModePicker: React.FC<SegmentedProps> = ({
  mode,
  homeDisabled,
  onPickHome,
  onPickGps,
  onPickCustom,
}) => (
  <HStack
    spacing={1}
    bg="#F2F2F2"
    borderRadius="100px"
    p="4px"
    align="stretch"
    w="100%">
    <SegButton selected={mode === 'home'} disabled={homeDisabled} onClick={onPickHome}>
      Home
    </SegButton>
    <SegButton selected={mode === 'gps'} onClick={onPickGps}>
      Current
    </SegButton>
    <SegButton selected={mode === 'custom'} onClick={onPickCustom}>
      Custom
    </SegButton>
  </HStack>
);

interface SegButtonProps {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const SegButton: React.FC<SegButtonProps> = ({selected, disabled, onClick, children}) => (
  <Box
    as="button"
    flex={1}
    h="36px"
    borderRadius="100px"
    bg={selected ? '#C9136B' : 'transparent'}
    color={selected ? 'white' : '#262626'}
    opacity={disabled ? 0.4 : 1}
    cursor={disabled ? 'not-allowed' : 'pointer'}
    transition="background-color 0.15s, color 0.15s"
    onClick={() => !disabled && onClick()}
    aria-disabled={disabled}>
    <TextG14 mt={0} lineHeight="1" fontWeight={selected ? '600' : '500'}>
      {children}
    </TextG14>
  </Box>
);

// Imperatively pans/centers the map whenever `target` changes. AdvancedMarker just renders
// the pin, but doesn't move the camera — we do it here so picking Home / Current / a
// suggestion smoothly recenters the view.
const MapPanController: React.FC<{target: PinCoords}> = ({target}) => {
  const map = useMap();
  const lastTarget = useRef<PinCoords | null>(null);
  useEffect(() => {
    if (!map) return;
    if (lastTarget.current?.lat === target.lat && lastTarget.current?.lng === target.lng) return;
    lastTarget.current = target;
    map.panTo(target);
  }, [map, target]);
  return null;
};

export default PickerModal;
